import { getMockTestList, setMockTestQueryData } from '@/actions/mock-test';
import { Badge, Col, List, Modal, Pagination, Skeleton } from 'antd';
import { useRouter } from 'next/router';
import { memo, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { timeConvert, timeToMinutes } from '../../helpers/timeHelper';
import PaidCourseModal, { MockTestDataType } from './common/PaidCourseModal';
import { slugTextConverter } from '@/helpers/dataHelper';
import useWidth from '@/helpers/widthUtil';

const MockTestItemGrid = ({
  total,
  loading,
  isCourseDetailComponent = false,
  course = null,
  courseSlug = null,
}) => {
  const dispatch = useDispatch();
  const router = useRouter();
  const { slug } = router.query;
  const { isMobile } = useWidth();

  const { query, mocktest } = useSelector((state: any) => ({
    query: state?.MockTestReducer?.query?.mockTestLanding,
    mocktest: state?.MockTestReducer?.mockTestLanding?.data,
  }));

  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [modalData, setModalData] = useState<MockTestDataType>();

  // Check if any mock-test is enrolled or not.
  const isEnrollAll = mocktest?.data?.every((test) => test?.is_enroll);

  useEffect(() => {
    dispatch(
      getMockTestList({
        data: {
          ...query,
          course: courseSlug,
        },
      })
    );
  }, [JSON.stringify([query.page, query.per_page])]);

  const paginationChange = (page, pageSize) => {
    if (!isCourseDetailComponent) {
      const modifiedQuery = { ...query, page: page, per_page: pageSize };

      dispatch(
        setMockTestQueryData({
          name: 'mockTestLanding',
          value: modifiedQuery,
        })
      );
    } else {
      const modifiedQuery = {
        page: page,
        per_page: pageSize,
        course: course?.slug,
      };
      dispatch(
        getMockTestList({
          data: {
            ...modifiedQuery,
          },
        })
      );
    }
  };

  const listData = [];
  for (let i = 0; i < mocktest?.data?.length; i++) {
    let data = mocktest?.data[i];
    listData.push({
      href: `/mock-test/${
        data?.has_completed_test
          ? 'reports'
          : data?.test_type == 'LISTENING'
          ? 'listening'
          : data?.test_type == 'WRITING'
          ? 'writing'
          : data?.test_type == 'READING'
          ? 'reading'
          : data?.test_type == 'SPEAKING'
          ? 'speaking'
          : 'normal'
      }/${data.slug}`,
      id: data?.id,
      image: data?.image,
      title: data?.title,
      course: data?.course?.name,
      duration: data?.duration && timeToMinutes(data?.duration) + ' Mins',
      full_marks: data?.total_mark,
      pass_mark: data?.pass_mark,
      test_type: data?.test_type,
      subject: data?.subject?.name,
      free: data?.is_free,
      currency: data?.currency,
      price: data?.price,
      discount_percentage: data?.discount_percentage,
      discount_price: data?.discount_price,
      slug: data?.slug,
      is_enroll: data?.is_enroll,
      has_completed_test: data?.has_completed_test,
    });
  }

  let locale = {
    emptyText: (
      <span>
        <p>
          <img
            src="/images/no_results.webp"
            height="250"
            width="250"
            className="mb-1"
          />
          <br />
          No mock test found
        </p>
      </span>
    ),
  };

  const handleCancel = () => {
    setIsModalOpen(false);
  };

  const handleLink = (item) => {
    if (!item.free) {
      setModalData(item);
      setIsModalOpen(true);
    } else {
      router.push(item.href);
    }
  };

  return (
    <>
      <Modal
        visible={isModalOpen}
        title="Buy Mock Test"
        onCancel={handleCancel}
        maskClosable={false}
        footer={''}
      >
        <PaidCourseModal modalData={modalData} />
      </Modal>
      <div
        className="mb-2 d-flex align-items-center justify-content-between flex-wrap gap-2"
        style={{
          borderRadius: '15px !important',
        }}
      >
        {loading ? (
          <>
            <Skeleton.Input active />
            {!isEnrollAll && <Skeleton.Input active size="large" />}
          </>
        ) : (
          <>
            <h4 className="m-0">{slugTextConverter(slug)}</h4>
            {!isEnrollAll && (
              <button
                type="button"
                className="btn btn-secondary font-weight-bold  border-danger m-0 "
                onClick={() => {
                  router.push(`/mock-test/course/payment/${slug}`);
                }}
              >
                Buy whole test
              </button>
            )}
          </>
        )}
      </div>

      {loading ? (
        <div className="mt-3">
          {[
            ...Array(1, 2, 3, 4, 5)?.map((i: number) => (
              <Skeleton.Button
                active
                className="w-100 mb-2"
                style={{
                  height: '120px',
                }}
              />
            )),
          ]}
        </div>
      ) : (
        <List
          className="mocktest-list"
          itemLayout="vertical"
          size="small"
          dataSource={listData}
          pagination={false}
          locale={locale}
          renderItem={(item) => (
            <List.Item
              className="mb-3 mt-3 mocktest-item cursor-pointer"
              key={item?.id}
              style={{
                boxShadow:
                  item?.free || item?.is_enroll
                    ? '0px 0px 4px #90ee90'
                    : '0px 0px 4px #DC4C64',
              }}
              onClick={() => handleLink(item)}
            >
              <List.Item.Meta
                className="py-2 m-0"
                avatar={
                  <img
                    width={112}
                    alt="logo"
                    src={
                      item.image
                        ? item.image
                        : 'https://learnsic.com/images/course.jpg'
                    }
                    height={100}
                  />
                }
                title={
                  <div className="d-flex justify-content-between">
                    <h5
                      style={{ color: '#284664', cursor: 'pointer' }}
                      className="text-break"
                    >
                      {item.title}
                    </h5>
                    {!isMobile && (
                      <Badge.Ribbon
                        key={item?.id}
                        text={
                          item?.is_enroll ? 'Enroll' : item?.free ? 'Enroll' : 'Paid'
                        }
                        color={item?.free || item?.is_enroll ? 'green' : 'red'}
                        style={{ marginRight: '-11px' }}
                      ></Badge.Ribbon>
                    )}
                  </div>
                }
                description={
                  <div className="row">
                    <div className="col ">
                      {!item.free && (
                        <div className="mock-course">
                          {item?.course && (
                            <span className="text-dark">Course:</span>
                          )}
                          <span className="cursor-pointer">
                            <span className="course">{item?.course}</span>
                          </span>
                        </div>
                      )}

                      {isMobile && (
                        <Badge.Ribbon
                          key={item?.id}
                          text={
                            item?.is_enroll
                              ? 'Enroll'
                              : item?.free
                              ? 'Enroll'
                              : 'Paid'
                          }
                          color={item?.free || item?.is_enroll ? 'green' : 'red'}
                          style={{ marginRight: '-11px' }}
                        ></Badge.Ribbon>
                      )}

                      <div className="mock-duration mt-2">
                        {item?.duration && (
                          <span className="text-dark ">Duration:</span>
                        )}
                        <span className="dur">
                          {parseFloat(item?.duration) > 60
                            ? timeConvert(item?.duration)
                            : item?.duration}
                        </span>
                      </div>
                      {!item.free && (
                        <div className="mock-subject mt-2">
                          <span className="text-dark">Subject:</span>{' '}
                          <span>
                            <span className="sub">{item?.subject}</span>
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                }
              />
            </List.Item>
          )}
        />
      )}
      {!loading && mocktest?.meta?.total > 10 && (
        <Col md="12" className="text-center mt-2 mb-5">
          <Pagination
            onChange={paginationChange}
            defaultCurrent={query?.page}
            total={total}
            // hideOnSinglePage={true}
            defaultPageSize={10}
          />
        </Col>
      )}
    </>
  );
};

export default memo(MockTestItemGrid);
